// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { SurveyType } from '../../../api/types';
import { hasInstanceLink } from '../../../kortlaegning/photo';
import type { ResourceIndex } from '../../../kortlaegning/resources';
import { shownView, VIEW_TEXT, viewCaption } from '../../../kortlaegning/view';
import { TextLink } from '../../TextLink';
import { ViewImage } from '../../kortlaegning/ViewImage';
import styles from './Inspector.module.css';

/** The image's longest drawn edge in the inspector (px), for the fetch size. */
const DISPLAY_SIZE = 480;

export interface ResourceImageProps {
  type: SurveyType;
  index: ResourceIndex;
  /** The focused part: its own image; none focused = the type's first part with one. */
  partCode: string | null;
  canEdit: boolean;
  /** Open a part's Foto evidence (`?part=&ev=foto`), where the image is chosen. */
  onOpen: (code: string) => void;
  /**
   * `hero`: the chosen image, full-bleed at the top of the tile (renders
   * nothing without one). `section`: the "Intet billede valgt" note in the
   * inspector's flow (renders nothing when an image is chosen).
   */
  placement: 'hero' | 'section';
}

/**
 * The inspector's "Ressourcebillede" (resource view record, frontend-r22.1):
 * the one chosen image (`sys:view`), cropped to its region — never a ranked
 * best frame standing in for it. A chosen image sits on top of the tile,
 * edge to edge (the tile's rounded corners crop it); none chosen reads
 * "Intet billede valgt" in the flow, and an editor of a scanned type is
 * pointed at Foto / 360°, where »Brug som ressourcebillede« sets it. A click
 * opens the owning part's Foto evidence.
 */
export function ResourceImage({ type, index, partCode, canEdit, onOpen, placement }: ResourceImageProps) {
  const { code, view } = shownView(type, partCode, index);

  if (placement === 'hero') {
    if (!view || !code) return null;
    const caption = viewCaption(view) + (partCode === null && type.parts.length > 1 ? ` · ${code}` : '');
    return (
      <button
        type="button"
        className={styles.heroPhoto}
        onClick={() => onOpen(code)}
        aria-label={`${caption} — åbn evidens`}
      >
        <ViewImage view={view} displaySize={DISPLAY_SIZE} maxFetch={1024} label={null} fit="cover" className={styles.viewImg} />
        <span className={styles.photoLabel}>{caption}</span>
      </button>
    );
  }

  if (view && code) return null;
  const first = type.parts.find((p) => hasInstanceLink(p))?.code ?? null;
  const openCode = code ?? (partCode !== null && type.parts.some((p) => p.code === partCode) ? partCode : first);
  return (
    <section className={styles.section} aria-label="Ressourcebillede">
      <h3 className={styles.sectionHead}>Ressourcebillede</h3>
      <p className={styles.sub}>
        {VIEW_TEXT.none}
        {canEdit && openCode !== null && (
          <>
            {' · '}
            <TextLink onClick={() => onOpen(openCode)}>Vælg under Foto eller 360°</TextLink>
          </>
        )}
      </p>
    </section>
  );
}
